import {
  PropsWithChildren,
  createContext,
  use,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router";
import {
  MD,
  useCompanies,
  useCompany as useMDCompany,
} from "../http/monatsdatenApi";
import { RemoteDataStatus } from "../types";
import { getRemoteDataStatus, parseIntParam } from "../utils";

interface CompanyContextType {
  company?: MD.Company;
  hasOnlyOneCompany: boolean;
  status: RemoteDataStatus;
  setCompanyId: (companyId?: MD.CompanyId) => void;
}

const CompanyContext = createContext<CompanyContextType>(undefined!);

const localStorageKeyCompany = "selected-company";

export const CompanyProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const { companyId: companyIdParam } = useParams();
  const localStorageCompanyId = localStorage.getItem(localStorageKeyCompany);
  const [companyId, setCompanyId] = useState<MD.CompanyId | undefined>(
    parseIntParam(companyIdParam),
  );

  const companyListAborter = useRef(new AbortController());
  const {
    data,
    isValidating: isCompanyListValidating,
    error: companyListError,
  } = useCompanies({ page: 1, limit: 2 }, companyListAborter.current.signal);
  const firstCompany = data?.items[0];
  const companyListStatus = getRemoteDataStatus({
    isValidating: isCompanyListValidating,
    error: companyListError,
  });

  const aborter = useRef(new AbortController());
  const {
    data: company,
    isValidating: isCompanyValidating,
    error: companyError,
  } = useMDCompany(companyId ?? 0, aborter.current.signal);
  const companyStatus = getRemoteDataStatus({
    isValidating: isCompanyValidating,
    error: companyError,
  });

  useEffect(
    () =>
      companyId
        ? localStorage.setItem(localStorageKeyCompany, companyId.toString())
        : localStorage.removeItem(localStorageKeyCompany),
    [companyId],
  );

  const value: CompanyContextType = {
    company,
    hasOnlyOneCompany: data?.items.length === 1,
    status: mergeStatuses([companyListStatus, companyStatus]),
    setCompanyId: (companyId?: MD.CompanyId) => {
      aborter.current.abort();
      aborter.current = new AbortController();
      setCompanyId(companyId);
      companyId ? navigate(`/${companyId}`) : navigate("/");
    },
  };

  useEffect(() => {
    if (companyId) return;
    value.setCompanyId(
      localStorageCompanyId
        ? parseInt(localStorageCompanyId)
        : firstCompany?.id,
    );
  }, [companyId, firstCompany]);

  return <CompanyContext value={value}>{children}</CompanyContext>;
};

const mergeStatuses = (statuses: RemoteDataStatus[]): RemoteDataStatus => {
  if (statuses.includes("failure")) return "failure";
  if (statuses.includes("validating")) return "validating";
  return "success";
};

export const useCompany = () => {
  return use(CompanyContext);
};
